import React, { createContext, type MutableRefObject, type ReactNode, useContext, useRef } from 'react';

interface TaskState {
	id: string | null;
	started: boolean;
	completed: boolean;
	error: Error | null;
}

// Default value hoisted outside to prevent recreation on every render
const DEFAULT_TASK_STATE: TaskState = {
	id: null,
	started: false,
	completed: false,
	error: null,
};

const VitalStatsTaskContext = createContext<React.MutableRefObject<TaskState> | null>(null);

/**
 * Provider component that manages VitalStats task state for QuickFind post-query tracking.
 * This provider should wrap the QuickFind component tree to enable VitalStats tracking
 * throughout the component hierarchy.
 *
 * Uses a shared ref to avoid recreating state on every render and prevent unnecessary rerenders.
 *
 * @example
 * ```tsx
 * <QuickFindPostQueryVitalStatsProvider>
 *   <QuickFindComponent />
 * </QuickFindPostQueryVitalStatsProvider>
 * ```
 */
export const QuickFindPostQueryVitalStatsProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
	// Use hoisted default to prevent object recreation on every render
	const taskStateRef = useRef<TaskState>(DEFAULT_TASK_STATE);

	return (
		<VitalStatsTaskContext.Provider value={taskStateRef}>{children}</VitalStatsTaskContext.Provider>
	);
};

/**
 * Hook to access the shared VitalStats task state ref.
 * Returns a stable ref that won't cause rerenders when mutated.
 */
export const useVitalStatsTaskState = (): MutableRefObject<TaskState> => {
	const taskStateRef = useContext(VitalStatsTaskContext);
	if (!taskStateRef) {
		throw new Error(
			'useVitalStatsTaskState must be used within QuickFindPostQueryVitalStatsProvider',
		);
	}
	return taskStateRef;
};
